import { QueryKey, useInfiniteQuery } from 'react-query';
import { get } from '../fetch';
import { appendPreviewDate } from '../helper';

const buildQueryString = (
  nextPage?: number,
  filter?: string,
  deliveryQuery?: string,
  store?: string,
  sort?: string,
  baseQuery?: string,
  query?: string,
) => {
  const tokenPart = nextPage && `from=${nextPage}`;
  const filterPart = filter && `filter=${filter}`;
  const deliveryQueryPart = deliveryQuery && `deliveryQuery=${deliveryQuery}`;
  const storePart = store && `store=${store}`;
  const sortPart = sort && `sort=${sort}`;
  const queryPart = query && `query=${encodeURIComponent(query)}`;
  const params = [
    filterPart,
    storePart,
    deliveryQueryPart,
    tokenPart,
    sortPart,
    queryPart || baseQuery,
  ]
    .filter((p) => !!p)
    .join('&');

  return (params && `?${params}`) || '';
};

const getSearch = (
  id?: string,
  filter?: string,
  deliveryQuery?: string,
  store?: string,
  sort?: string,
  baseQuery?: string,
  query?: string,
) => {
  if (id) {
    return [
      ['by-category', id, filter, deliveryQuery, store, sort, baseQuery],
      `/api/product-listing/navigation/${id}`,
    ];
  } else if (baseQuery) {
    return [
      ['by-query', filter, deliveryQuery, store, sort, baseQuery],
      `/api/product-listing/query`,
    ];
  } else if (query) {
    return [
      ['by-search', query, filter, deliveryQuery, store, sort, baseQuery],
      `/api/product-listing/search`,
    ];
  }
  return [undefined, undefined];
};

export const useCategoryQuery = (
  id?: string,
  initialData?: ICategory,
  pageToken?: number,
  filter?: string,
  deliveryQuery?: string,
  store?: string,
  sort?: string,
  baseQuery?: string,
  query?: string,
) => {
  const [key, url] = getSearch(id, filter, deliveryQuery, store, sort, baseQuery, query);

  const infiniteQuery = useInfiniteQuery(
    key as QueryKey,
    ({ pageParam: token }) =>
      get<ICategory>(
        appendPreviewDate(
          `${url}${buildQueryString(token || pageToken, filter, deliveryQuery, store, sort, baseQuery, query)}`,
        ),
      ),
    {
      initialData:
        (initialData && {
          pages: [initialData],
          pageParams: [initialData.nextPage],
        }) ||
        undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage;
      },
    },
  );

  // WORKAROUND
  // Sometimes the sort order of the products
  // change when initial data is set on the the server.
  // This fix skips react-query on the server, hence it is
  // not needed, just the data
  if (SSR && initialData) {
    return {
      data: {
        pages: [initialData],
        pageParams: [initialData?.nextPage],
      },
      fetchNextPage: () => undefined,
      isFetching: false,
      isFetchingNextPage: false,
    };
  }

  return infiniteQuery;
};
